import api from 'connections/api';
import routes_api from 'connections/routes_api';

// Consolidated
export const fetchFlowsConsolidated = (params = {}) => {
  return api.get(routes_api.finance_flows_consolidated, { params });
};

// Flow CRUD Operations
export const fetchFinancialFlows = async (params = {}) => {
  try {
    const response = await api.get(routes_api.finance_flows, { params });
    return response.data.results || [];
  } catch (error) {
    console.error('Failed to fetch financial flows:', error);
    throw error;
  }
};

export const createFlowTransaction = async (flowData) => {
  try {
    const response = await api.post(routes_api.finance_flows, flowData);
    return response.data;
  } catch (error) {
    console.error('Failed to create flow transaction:', error);
    throw error;
  }
};

export const updateFlowTransaction = async (id, flowData) => {
  try {
    const response = await api.put(`${routes_api.finance_flows}${id}/`, flowData);
    return response.data;
  } catch (error) {
    console.error('Failed to update flow transaction:', error);
    throw error;
  }
};

export const deleteFlowTransaction = async (id) => {
  try {
    const response = await api.delete(`${routes_api.finance_flows}${id}/`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete flow transaction:', error);
    throw error;
  }
};

// ##### Flow preliminary (staging) — mirror of FlowTransactions + status/source/external_id
export const fetchFlowPreliminaryTransactions = async (params = {}) => {
  try {
    const response = await api.get(routes_api.finance_flows_preliminary, {
      params: { limit: 500, ...params },
    });
    return response.data.results ?? response.data ?? [];
  } catch (error) {
    console.error('Failed to fetch preliminary flow transactions:', error);
    throw error;
  }
};

export const createFlowPreliminaryTransaction = async (payload) => {
  const response = await api.post(routes_api.finance_flows_preliminary, payload);
  return response.data;
};

export const updateFlowPreliminaryTransaction = async (id, payload) => {
  const response = await api.patch(`${routes_api.finance_flows_preliminary}${id}/`, payload);
  return response.data;
};

export const deleteFlowPreliminaryTransaction = async (id) => {
  await api.delete(`${routes_api.finance_flows_preliminary}${id}/`);
};

/** Bulk create preliminary rows (atomic on server; skips duplicate user+external_id). */
export const createManyFlowPreliminaryTransactions = async (records) => {
  const response = await api.post(routes_api.finance_flows_preliminary_create_many, records);
  return response.data;
};

/**
 * Delete preliminary rows for the current user.
 * @param {{ scope: 'all' } | { scope: 'range', from_date: string, to_date: string }} payload
 */
export const deleteManyFlowPreliminaryTransactions = async (payload) => {
  const response = await api.post(routes_api.finance_flows_preliminary_delete_many, payload);
  return response.data;
};

// Get Years
export const fetchFlowsYears = () => {
  return api.get(routes_api.finance_flows_getyears);
};


// ##### METADATA CATEGORIES
export const fetchMetadataCategories = async () => {
  try {
    const response = await api.get(routes_api.finance_flows_categories);
    return response.data.results || [];
  } catch (error) {
    console.error('Error fetching metadata categories:', error);
    return [];
  }
};

export const fetchMetadataCategoriesHierarchical = async () => {
  try {
    const response = await api.get(routes_api.finance_flows_categories_hierarchical);
    return response.data || {};
  } catch (error) {
    console.error('Error fetching hierarchical categories:', error);
    return {};
  }
};

// ##### USER CURRENCIES
export const fetchUserCurrencies = async () => {
  try {
    const response = await api.get(routes_api.finance_currencies);
    return response.data?.results ?? response.data ?? [];
  } catch (error) {
    console.error('Failed to fetch user currencies:', error);
    throw error;
  }
};

export const fetchAvailableCurrencies = async () => {
  try {
    const response = await api.get(routes_api.finance_currencies_available);
    return response.data ?? [];
  } catch (error) {
    console.error('Failed to fetch available currencies:', error);
    throw error;
  }
};

export const createUserCurrency = async (data) => {
  try {
    const response = await api.post(routes_api.finance_currencies, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create user currency:', error);
    throw error;
  }
};

export const deleteUserCurrency = async (id) => {
  try {
    await api.delete(`${routes_api.finance_currencies}${id}/`);
  } catch (error) {
    console.error('Failed to delete user currency:', error);
    throw error;
  }
};

// ##### FLOW ACCOUNTS CRUD OPERATIONS
export const fetchFlowAccounts = async () => {
  try {
    const response = await api.get(routes_api.finance_accounts);
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch flow accounts:', error);
    throw error;
  }
};

export const createFlowAccount = async (accountData) => {
  try {
    const response = await api.post(routes_api.finance_accounts, accountData);
    return response.data;
  } catch (error) {
    console.error('Failed to create flow account:', error);
    throw error;
  }
};

export const updateFlowAccount = async (id, accountData) => {
  try {
    const response = await api.patch(`${routes_api.finance_accounts}${id}/`, accountData);
    return response.data;
  } catch (error) {
    console.error('Failed to update flow account:', error);
    throw error;
  }
};

export const deleteFlowAccount = async (id) => {
  try {
    const response = await api.delete(`${routes_api.finance_accounts}${id}/`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete flow account:', error);
    throw error;
  }
};

// ##### FLOW ACCOUNTS HISTORY CRUD OPERATIONS
export const fetchFlowAccountsHistory = async (params = {}) => {
  try {
    const response = await api.get(routes_api.finance_accounts_history, { params });
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch flow accounts history:', error);
    throw error;
  }
};

export const createFlowAccountHistory = async (historyData) => {
  try {
    const response = await api.post(routes_api.finance_accounts_history, historyData);
    return response.data;
  } catch (error) {
    console.error('Failed to create flow account history:', error);
    throw error;
  }
};

export const updateFlowAccountHistory = async (id, historyData) => {
  try {
    const response = await api.put(`${routes_api.finance_accounts_history}${id}/`, historyData);
    return response.data;
  } catch (error) {
    console.error('Failed to update flow account history:', error);
    throw error;
  }
};

// ##### BATCH OPERATIONS
export const deleteAllFlowAccountHistory = async () => {
  try {
    const response = await api.delete(`${routes_api.finance_accounts_history}delete_all/`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete all flow account history:', error);
    throw error;
  }
};

export const createBatchFlowAccountHistory = async (historyRecords) => {
  try {
    const response = await api.post(`${routes_api.finance_accounts_history}create_batch/`, historyRecords);
    return response.data;
  } catch (error) {
    console.error('Failed to create batch flow account history:', error);
    throw error;
  }
};

export const deleteFlowAccountHistory = async (id) => {
  try {
    const response = await api.delete(`${routes_api.finance_accounts_history}${id}/`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete flow account history:', error);
    throw error;
  }
};